// #17 read-only "API Logs" resource for the Applied GA admin dashboard.
// Every outbound third-party call the portal makes (production) or records
// without sending (every other environment) shows up here. See
// app/lib/api-dispatch.js.
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Box, Paper, Typography } from "@mui/material";
import {
  Datagrid,
  DateField,
  List,
  Show,
  SimpleShowLayout,
  TextField,
  useRecordContext,
} from "react-admin";
import MockBanner from "./MockBanner";

// A call is a "failure" only if we actually tried to send and it failed (a real
// error, or a non-2xx response in production). Outside production nothing is
// sent, so we assume success (a 200) and show a check. This avoids a noisy
// marker on every recorded request.
const isFailure = (record) =>
  !!record?.error || (record?.sent && record?.responseStatus >= 400);

const StatusField = () => {
  const record = useRecordContext();
  if (isFailure(record)) {
    const title = record?.error || `HTTP ${record?.responseStatus}`;
    return <CancelIcon color="error" fontSize="small" titleAccess={title} />;
  }
  return <CheckCircleIcon color="success" fontSize="small" titleAccess="OK" />;
};

const escapeHtml = (s) =>
  String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// Lightweight JSON syntax highlighting (no extra dependency). Escapes first,
// then wraps tokens in colored spans.
const highlightJson = (obj) => {
  const json = escapeHtml(JSON.stringify(obj, null, 2));
  return json.replace(
    /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
    (match) => {
      let cls = "jh-number";
      if (/^"/.test(match)) cls = /:$/.test(match) ? "jh-key" : "jh-string";
      else if (/true|false/.test(match)) cls = "jh-boolean";
      else if (/null/.test(match)) cls = "jh-null";
      return `<span class="${cls}">${match}</span>`;
    }
  );
};

const jsonSx = {
  p: 1.5,
  mt: 0.5,
  overflow: "auto",
  fontSize: "0.8rem",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  "& .jh-key": { color: "#0b5cad" },
  "& .jh-string": { color: "#0a7d33" },
  "& .jh-number": { color: "#9a5cd0" },
  "& .jh-boolean": { color: "#b3261e" },
  "& .jh-null": { color: "#6e7781" },
};

const JsonBlock = ({ data }) => (
  <Paper
    variant="outlined"
    component="pre"
    sx={jsonSx}
    dangerouslySetInnerHTML={{ __html: highlightJson(data) }}
  />
);

// Render only the formatting tags WE generate (<strong>, <br>); everything else
// (including anything an agent typed) is escaped to plain text, so a recorded
// payload can't inject markup/script into this admin page.
const renderOurHtml = (html) => {
  const escaped = escapeHtml(html);
  return escaped.replace(
    /&lt;(\/?)(strong|br)\s*\/?&gt;/gi,
    (_m, slash, tag) => `<${slash}${tag.toLowerCase()}>`
  );
};

// The full request body as highlighted JSON. For Freshservice the ticket body
// is HTML, so we also render it as readable rich text below the JSON. The
// rich-text block is Freshservice-only and guarded on the description being a
// string, so other services (e.g. Docusign later) never trip it.
const RequestBodyField = () => {
  const record = useRecordContext();
  const body = record?.requestBody;
  if (!body || typeof body !== "object") return null;

  const showTicketBody =
    record?.service === "freshservice" && typeof body.description === "string";

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary">
        Request body (recorded; this is what we would send)
      </Typography>
      <JsonBlock data={body} />

      {showTicketBody ? (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Description (ticket body)
          </Typography>
          <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5 }}>
            <Box
              sx={{ fontSize: "0.85rem", lineHeight: 1.7 }}
              dangerouslySetInnerHTML={{ __html: renderOurHtml(body.description) }}
            />
          </Paper>
        </Box>
      ) : null}
    </Box>
  );
};

// The response we got back. Only present when the call was actually sent (i.e.
// production); shows the numeric HTTP status and the response body.
const ResponseField = () => {
  const record = useRecordContext();
  const status = record?.responseStatus;
  const body = record?.responseBody;
  const hasBody = body !== undefined && body !== null && body !== "";
  if (status == null && !hasBody) return null;
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary">
        Response
      </Typography>
      {status != null ? (
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
          HTTP {status}
        </Typography>
      ) : null}
      {hasBody ? (
        typeof body === "object" ? (
          <JsonBlock data={body} />
        ) : (
          <Paper variant="outlined" component="pre" sx={jsonSx}>
            {String(body)}
          </Paper>
        )
      ) : null}
    </Box>
  );
};

export const ApiLogList = () => (
  <>
    <MockBanner />
    <List
      sort={{ field: "createdAt", order: "DESC" }}
      exporter={false}
      title="API Logs"
    >
      <Datagrid rowClick="show" bulkActionButtons={false}>
        <DateField source="createdAt" label="When" showTime />
        <TextField source="service" />
        <TextField source="method" />
        <TextField source="url" />
        <StatusField label="Status" />
      </Datagrid>
    </List>
  </>
);

export const ApiLogShow = () => (
  <Show title="API Log">
    <Box sx={{ p: 2 }}>
      <MockBanner />
    </Box>
    <SimpleShowLayout>
      <TextField source="service" />
      <TextField source="method" />
      <TextField source="url" />
      <StatusField label="Status" />
      <DateField source="createdAt" label="When" showTime />
      <TextField source="error" emptyText="(none)" />
      <RequestBodyField />
      <ResponseField />
    </SimpleShowLayout>
  </Show>
);

export default ApiLogList;
